/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React, { useState, useMemo, useCallback } from "react";
import { NavigableBreadcrumbLink } from "../../../components/page_breadcrumbs/navigable_breadcrumb_link";
import {
  Breadcrumbs,
  Typography,
  FormControl,
  Select,
  InputAdornment,
  IconButton,
  Box,
  Divider,
  Tooltip,
} from "@mui/material";
import {
  MobileUserActivityContainer,
  StyledSearchBar,
  StyledNameLink,
  StyledDate,
} from "./styles";
import {
  StyledBreadCrumbContainer,
  StyledLink,
  StyledInputLabel
} from "../employee_visibility/styles";
import DropdownSearchBar from '../../../components/dorpdown_searchbar'
import { DropdownInfiniteLoader } from "../../../components/dropdown_infinite_loader";
import { SelectedMenuItem } from '../../../components/selected_menu_item/index'
import { useInfiniteQuery, useQuery, keepPreviousData } from "@tanstack/react-query";
import {
  viewTypographyStyles,
  selectBaseStyles,
} from "../styles";
import { UpdateButton } from "../../../components/update_button";
import { IndexTable } from "../../../components/index_table";
import camelize from "../../../utils/camelize";
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import api from '../../../api'
import authenticatedFetch from '../../../api/authenticatedFetch'
import { Loader } from "../../../components/loader";
import { useDebounce } from "../../../utils/useDebounce";
import { formatDate } from "../../../utils/formatDate";
import { buildQueryParams } from "../../../utils/buildQueryParams";
import { useNotificationContext } from "../../../contexts/notificationContext";
import dayjs from "dayjs";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { datePickerStyles } from "../styles";
import { ResetButton } from "../../../components/reset_button";
import { FilterContainer } from "../../../components/filter_container";
import { useNavigate } from "react-router-dom";
import { UserActivityLogModal } from "../../../components/user_activity_log_modal";
import { useQueryClient } from "@tanstack/react-query";
import { StyledMenuItem } from "../../../components/styled_menu_item";
import { StyledTitle } from "../../employee/styles";
import { useSelector } from "react-redux";
import { checkAdmin } from "../../../utils/checkAdmin";
import { hasSingleItem } from "../../../utils/hasSingleItem";
import { getSingleItemValue } from "../../../utils/getSingleItemValue";
import { RenderList } from "../../../components/render_list";
import { Query_Keys } from "../../../lib/queryKeys";
import { PerPage } from "../../../constants/perPage";
import {
  getDropdownNextPageParam,
  mergeUniqueByKey,
  normalizeRestaurant,
  normalizeRestaurantGroup,
} from "../../../utils/dropdownInfiniteQuery";
import { useInfiniteScrollObserver } from "../../../hooks/useInfiniteScrollObserver";
import { isValidDayjsValue } from "../../../utils/isValidDayjsValue";
import { isValidCustomDateRange } from "../../../utils/isValidCustomDateRange";

const ALL_VALUE = 'All';
const defaultFilter = {
  dateFilter: ALL_VALUE,
  customDateFrom: null,
  customDateTo: null,
  restaurant_id: [ALL_VALUE],
  restaurant_group_id: [ALL_VALUE],
  platform: ALL_VALUE
}

export const MobileUserActivityPage = () => {
  const [filters, setFilters] = useState(defaultFilter)
  const [appliedFilters, setAppliedFilters] = useState(defaultFilter)
  const [filtersChanged, setFiltersChanged] = useState(false)
  const [restaurantGroupSearch, setRestaurantGroupSearch] = useState('')
  const [restaurantSearch, setRestaurantSearch] = useState('')
  const [search, setSearch] = useState('')
  const [itemsPerPage, setItemsPerPage] = useState(25)
  const [page, setPage] = useState(1)
  const [sortBy, setSortBy] = useState('id')
  const [sortOrder, setSortOrder] = useState('desc')
  const [csvLoading, setCsvLoading] = useState(false)
  const debouncedSearchTerm = useDebounce(search, 300);
  const showNotification = useNotificationContext()
  const navigate = useNavigate();
  const [selectedNetworkGuest, setSelectedNetworkGuest] = useState(null)
  const [openActivityLog, setOpenActivityLog] = useState(false)
  const [modalPage, setModalPage] = useState(1)
  const [modalPerPage, setModalPerPage] = useState(25)
  const queryClient = useQueryClient();
  const [restaurantOptionsCache, setRestaurantOptionsCache] = useState([])
  const [restaurantGroupOptionsCache, setRestaurantGroupOptionsCache] = useState([])
  const { admin } = useSelector((state) => state.admin);
  const currentAdmin = checkAdmin(admin?.formatted_roles)
  const ifSuperAdmin = !(currentAdmin?.role === 'admin' || currentAdmin?.role === 'viewer')
  const debouncedRestaurantGroupSearch = useDebounce(restaurantGroupSearch.trim(), 300);
  const debouncedRestaurantSearch = useDebounce(restaurantSearch.trim(), 300);

  const fetchRestaurantGroupList = async ({ pageParam = 1 }) => {
    const response = await api.get("/v1/admin/dropdowns/restaurant_groups_list", {
      params: {
        page: pageParam,
        per_page: PerPage,
        search: debouncedRestaurantGroupSearch || undefined,
      },
    });
    return response?.data?.map(normalizeRestaurantGroup) || [];
  };

  const {
    data: restaurantGroups = [],
    isFetching: restaurantGroupFetching,
    isFetchingNextPage: restaurantGroupsFetchingNextPage,
    fetchNextPage: fetchNextRestaurantGroupsPage,
    hasNextPage: hasNextRestaurantGroupsPage,
  } = useInfiniteQuery({
    queryKey: Query_Keys.restaurant_groups_dropdown({ search: debouncedRestaurantGroupSearch }),
    queryFn: fetchRestaurantGroupList,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: (data) => data.pages.flat(),
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  const fetchRestaurantList = async ({ pageParam = 1 }) => {
    const response = await api.get('/v1/admin/dropdowns/restaurants_list', {
      params: {
        restaurant_group_ids: filters?.restaurant_group_id?.includes(ALL_VALUE) ? [] : filters?.restaurant_group_id,
        page: pageParam,
        per_page: PerPage,
        search: debouncedRestaurantSearch || undefined,
      }
    })
    return response?.data?.map(normalizeRestaurant) || [];
  }

  const {
    data: restaurants = [],
    isFetching: restaurantFetching,
    isFetchingNextPage: restaurantsFetchingNextPage,
    fetchNextPage: fetchNextRestaurantsPage,
    hasNextPage: hasNextRestaurantsPage,
  } = useInfiniteQuery({
    queryKey: Query_Keys.restaurants_dropdown({
      restaurant_group_id: filters?.restaurant_group_id,
      search: debouncedRestaurantSearch,
    }),
    queryFn: fetchRestaurantList,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: (data) => data.pages.flat(),
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  })

  const { rootRef: restaurantGroupMenuRef, targetRef: restaurantGroupLoaderRef } = useInfiniteScrollObserver({
    fetchNextPage: fetchNextRestaurantGroupsPage,
    hasNextPage: hasNextRestaurantGroupsPage,
    isFetchingNextPage: restaurantGroupsFetchingNextPage,
  })

  const { rootRef: restaurantMenuRef, targetRef: restaurantLoaderRef } = useInfiniteScrollObserver({
    fetchNextPage: fetchNextRestaurantsPage,
    hasNextPage: hasNextRestaurantsPage,
    isFetchingNextPage: restaurantsFetchingNextPage,
  })

  const fetchMobileUserActivity = async () => {
    const formattedParamters = formattedFilters(appliedFilters)
    const response = await api.get('/v1/admin/reports/mobile_user_activities', {
      params: {
        per_page: itemsPerPage,
        page: page,
        sort_by: sortBy,
        sort_order: sortOrder,
        search: debouncedSearchTerm,
        ...formattedParamters
      }
    })
    return {data: response?.data, pageCount: response?.headers["total-count"]}
  }

  const { data: mobileUserActivityData, isFetching} = useQuery({
    queryKey: Query_Keys.mobile_user_activity({
      queryFilter: appliedFilters,
      search: debouncedSearchTerm,
      page: page,
      itemsPerPage: itemsPerPage,
      sortBy,
      sortOrder,
    }),
    queryFn: fetchMobileUserActivity,
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData
  })

  const fetchDatePresets = async () => {
    const response = await api.get('/v1/admin/reports/mobile_user_activity_date_presets')
    return response?.data || {}
  }

  const { data: datePresets = {} } = useQuery({
    queryKey: Query_Keys.mobile_user_activity_date_presets,
    queryFn: fetchDatePresets,
    staleTime: 10 * 60 * 1000,
  })

  const fetchUserActivityLog = async (id) => {
    const response = await api.get(`/v1/admin/reports/${id}/mobile_user_activities_detail`, {
      params: {
        page: modalPage,
        per_page: modalPerPage,
      }
    })
    return { data: response?.data, pageCount: response?.headers["total-count"] }
  }

  const { data: userActivityLog, isLoading: activityLogLoading } = useQuery({
    queryKey: Query_Keys.user_activity_log({
      id: selectedNetworkGuest,
      page: modalPage,
      perPage: modalPerPage,
    }),
    queryFn: () => fetchUserActivityLog(selectedNetworkGuest),
    staleTime: 10 * 60 * 1000,
    enabled: openActivityLog,
  })

  const handleChange = () => {
    setFiltersChanged(false)
    setAppliedFilters(filters)
    setPage(1)
    setItemsPerPage(25)
  }

  React.useEffect(() => {
    setRestaurantGroupOptionsCache((currentOptions) => mergeUniqueByKey(currentOptions, restaurantGroups, 'label'));
  }, [restaurantGroups]);

  React.useEffect(() => {
    setRestaurantOptionsCache((currentOptions) => mergeUniqueByKey(currentOptions, restaurants, 'id'));
  }, [restaurants]);

  const hasSingleAvailableRestaurantGroup = !debouncedRestaurantGroupSearch && hasSingleItem(restaurantGroups);
  const hasSingleAvailableRestaurant = !debouncedRestaurantSearch && hasSingleItem(restaurants);

  React.useEffect(() => {
    if (hasSingleAvailableRestaurantGroup) {
      const singleRestaurantGroup = getSingleItemValue(restaurantGroups);
      setFilters(prev => ({
        ...prev,
        restaurant_group_id: [singleRestaurantGroup.label]
      }));
    }
  }, [hasSingleAvailableRestaurantGroup, restaurantGroups]);

  React.useEffect(() => {
    if (hasSingleAvailableRestaurant) {
      const singleRestaurant = getSingleItemValue(restaurants);
      setFilters(prev => ({
        ...prev,
        restaurant_id: [singleRestaurant.id]
      }));
    }
  }, [hasSingleAvailableRestaurant, restaurants]);

  const handleRestaurantGroupChange = (event) => {
    const {
      target: { value },
    } = event;
    setFiltersChanged(true);

    if ((value?.length === 1 && value.includes(ALL_VALUE)) || value?.length === 0) {
      setFilters({ ...filters, restaurant_group_id: [ALL_VALUE], restaurant_id: [ALL_VALUE] });
    }
    else {
      const newRestaurantGroupIds = value.filter(val => val !== ALL_VALUE);
      setFilters({ ...filters, restaurant_group_id: newRestaurantGroupIds, restaurant_id: [ALL_VALUE] });
    }
  }

  const handleRestaurantChange = (event) => {
    const {
      target: { value },
    } = event;
    setFiltersChanged(true);

    if ((value?.length === 1 && value.includes(ALL_VALUE)) || value?.length === 0) {
      setFilters({ ...filters, restaurant_id: [ALL_VALUE] });
    }
    else {
      const newRestaurantIds = value.filter(val => val !== ALL_VALUE);
      setFilters({ ...filters, restaurant_id: newRestaurantIds });
    }
  }

  const handleDateChange = useCallback((field, value) => {
    setFilters((prev) => {
      const nextFilters = {
        ...prev,
        [field]: value,
      };

      if (field === "dateFilter" && value !== "Custom Date") {
        nextFilters.customDateFrom = null;
        nextFilters.customDateTo = null;
      }

      if (field === "customDateFrom") {
        if (!value) {
          nextFilters.customDateTo = null;
        } else if (isValidDayjsValue(value) && !isValidDayjsValue(prev.customDateTo)) {
          nextFilters.customDateTo = dayjs();
        }
      }

      return nextFilters;
    });
    setFiltersChanged(true);
  }, []);

  const handlePlatformChange = (event) => {
    setFilters({...filters, platform: event.target.value})
    setFiltersChanged(true);
  }

  const handleRestaurantGroupClear = () => {
    setFilters({...filters, restaurant_group_id: [ALL_VALUE]})
    setFiltersChanged(true)
  }

  const handleRestaurantClear = () => {
    setFilters({...filters, restaurant_id: [ALL_VALUE]})
    setFiltersChanged(true)
  }

  const handlePlatformClear = () => {
    setFilters({...filters, platform: ALL_VALUE})
    setFiltersChanged(true)
  }

  const handleDateClear = () => {
    setFilters({
      ...filters,
      dateFilter: ALL_VALUE,
      customDateFrom: null,
      customDateTo: null,
    })
    setFiltersChanged(true)
  }

  const getRestaurantGroupName = (id) => {
    if (id === ALL_VALUE) return 'All';
    const restaurantGroup = restaurantGroupOptionsCache?.find((res) => res.label === id)
    return restaurantGroup?.value ? restaurantGroup?.value : ''
  }

  const getRestaurantName = (id) => {
    if (id === ALL_VALUE) return 'All';
    const restaurant = restaurantOptionsCache?.find((res) => res.id === id)
    return restaurant?.name ? restaurant?.name : ''
  }

  const getPlatformName = (name) => {
    if (name === ALL_VALUE) return 'All';
    const platformName = platform?.find((platformType) => platformType?.value === name)
    return platformName?.label || ''
  }

  const formattedFilters = (filterValues = appliedFilters) => {
    const params = {};
    const today = dayjs();
    const presetKeyByLabel = {
      Today: 'today',
      Yesterday: 'yesterday',
      'This Week': 'this_week',
      'Last 7 Days': 'last_7_days',
      'This Month': 'this_month',
      'Last Month': 'last_month',
    };

    // Date range handling
    if (filterValues?.dateFilter !== "Custom Date" && filterValues?.dateFilter !== ALL_VALUE) {
      const presetKey = presetKeyByLabel[filterValues?.dateFilter];
      if (presetKey && datePresets?.[presetKey]?.from && datePresets?.[presetKey]?.to) {
        params.last_used_date_range = {
          from: datePresets[presetKey].from,
          to: datePresets[presetKey].to,
        };
      } else {
        switch (filterValues?.dateFilter) {
          case "Today":
            params.last_used_date_range = {
              from: today.format("DD/MM/YYYY"),
              to: today.format("DD/MM/YYYY"),
            };
            break;
          case "Last 7 Days":
            params.last_used_date_range = {
              from: today.subtract(7, "day").format("DD/MM/YYYY"),
              to: today.format("DD/MM/YYYY"),
            };
            break;
          case "Yesterday":
            params.last_used_date_range = {
              from: today.subtract(1, "day").format("DD/MM/YYYY"),
              to: today.subtract(1, "day").format("DD/MM/YYYY"),
            };
            break;
          case "This Week":
            params.last_used_date_range = {
              from: today.startOf("week").format("DD/MM/YYYY"),
              to: today.endOf("week").format("DD/MM/YYYY"),
            };
            break;
          case "This Month":
            params.last_used_date_range = {
              from: today.startOf("month").format("DD/MM/YYYY"),
              to: today.endOf("month").format("DD/MM/YYYY"),
            };
            break;
          case "Last Month":
            params.last_used_date_range = {
              from: today
                .subtract(1, "month")
                .startOf("month")
                .format("DD/MM/YYYY"),
              to: today.subtract(1, "month").endOf("month").format("DD/MM/YYYY"),
            };
            break;
          default:
            break;
        }
      }
    } else if (
      filterValues?.dateFilter === "Custom Date" &&
      isValidCustomDateRange(filterValues?.customDateFrom, filterValues?.customDateTo)
    ) {
      params.last_used_date_range = {
        from: dayjs(filterValues?.customDateFrom).format("DD/MM/YYYY"),
        to: dayjs(filterValues?.customDateTo).format("DD/MM/YYYY"),
      };
    }

    if (!filterValues?.restaurant_group_id?.includes(ALL_VALUE)) {
      params.restaurant_group_ids = filterValues?.restaurant_group_id;
    }
    if (!filterValues?.restaurant_id?.includes(ALL_VALUE)) {
      params.restaurant_ids = filterValues?.restaurant_id;
    }

    if(filterValues?.platform !== ALL_VALUE) {
      params.platform = filterValues?.platform
    }

    return params;
  }

  const getRestaurantDisplayValue = () => {
    if (!filters?.restaurant_id || filters?.restaurant_id?.length === 0) {
      return 'All';
    }

    if (filters?.restaurant_id?.includes(ALL_VALUE)) {
      return 'All';
    }

    if (filters?.restaurant_id?.length === 1 && restaurantOptionsCache) {
      const restaurant = restaurantOptionsCache.find(r => r.id === filters.restaurant_id[0]);
      return restaurant ? restaurant.name : 'All';
    }

    return `${filters?.restaurant_id?.length} Restaurants Selected`;
  }

  const getRestaurantGroupDisplayValue = () => {
    if (!filters?.restaurant_group_id || filters?.restaurant_group_id?.length === 0) {
      return 'All';
    }

    if (filters?.restaurant_group_id?.includes(ALL_VALUE)) {
      return 'All';
    }

    if (filters?.restaurant_group_id?.length === 1 && restaurantGroupOptionsCache) {
      const restaurantGroup = restaurantGroupOptionsCache.find(r => r.label === filters.restaurant_group_id[0]);
      return restaurantGroup ? restaurantGroup.value : 'All';
    }

    return `${filters?.restaurant_group_id?.length} Restaurant Groups Selected`;
  }

  const handleSearch = (searchQuery) => {
    setSearch(searchQuery)
    setPage(1)
  }

  const handleSortChange = (nextSortBy, nextSortOrder) => {
    setSortBy(nextSortBy)
    setSortOrder(nextSortOrder)
    setPage(1)
  }

  const handleReset = () => {
    setFilters(defaultFilter)
    setAppliedFilters(defaultFilter)
    setFiltersChanged(false)
    setItemsPerPage(25);
    setPage(1);
  }

  const Days = useMemo(() => [
    "Today",
    "Yesterday",
    "This Week",
    "Last 7 Days",
    "This Month",
    "Last Month",
    "Custom Date",
  ], []);

  const platform = [
    {
      label: 'iOS',
      value: 'ios'
    },
    {
      label: 'Android',
      value: 'android'
    }
  ]

  const handleExportCsv = async () => {
    try {
      setCsvLoading(true);
      let formattedParamters = formattedFilters(appliedFilters)
      const parameter = {
        search: debouncedSearchTerm,
        ...formattedParamters
      }
      const query = buildQueryParams(parameter)
      const url = `${process.env.REACT_APP_API_BASE_URL}/v1/admin/csv_reports/export?report=mobile_user_activities&${query}`;

      const { response } = await authenticatedFetch(url, {
        method: 'GET',
        defaultErrorMessage: 'Failed to download CSV'
      });

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = downloadUrl;
      let date = new Date
      let filename = `mobile_user_activities_${dayjs(date).format('YYYY-MM-DD')}.csv`

      a.download = filename;
      a.click();

      window.URL.revokeObjectURL(downloadUrl);

      showNotification('CSV downloaded successfully', 'success');
    } catch (error) {
      console.error('CSV download error:', error);
      showNotification(error?.message || 'Failed to download CSV', 'error');
    } finally {
      setCsvLoading(false);
    }
  }

  const handleAcitivityLog = (networkGuestId) => {
    setSelectedNetworkGuest(networkGuestId)
    setOpenActivityLog(true)
    setModalPage(1)
    setModalPerPage(25)
  }

  const handleNavigate = (firstName, lastName) => {
    queryClient.removeQueries({ queryKey: ["global_employee_search"] });
    navigate('/admin/reports/global_search', {state: {user: `${firstName} ${lastName}`}})
  }

  const columnOptions = [
    { key: "id", sortable: true },
    { key: "name", sortable: true },
    { key: "phone_number", sortable: true },
    { key: "platform", sortable: true },
    { key: "app_version", sortable: true },
    { key: "os_version", sortable: true },
    { key: "restaurant", sortable: true },
    { key: "first_sync_date", sortable: true },
    { key: "last_used_date", sortable: true },
    { key: "days_since_last_used", sortable: true },
  ];

  const columns = columnOptions?.map((column) => {
    return {
      key: column.key,
      label: camelize(column.key),
      sortable: column.sortable,
    };
  });

  const TableRows = (data) => {
    return data?.map((record) => {
      return {
        id: record?.id,
        name: {
          display: (
            record?.first_name || record?.last_name ? (
              <StyledNameLink
                disabled={ifSuperAdmin}
                onClick={() => handleNavigate(record?.first_name, record?.last_name)}
              >
                {`${record?.first_name} ${record?.last_name}`}
              </StyledNameLink>
            ) : (
              '-'
            )
          ),
          sortValue: [record?.first_name, record?.last_name].filter(Boolean).join(' ') || '-'
        },
        phone_number: record?.phone_number || '-',
        platform: record?.platform || '-',
        first_sync_date: formatDate(record?.first_sync_date, record?.time_zone_abbr),
        last_used_date: {
          display: (
            <StyledDate
              onClick={() => handleAcitivityLog(record?.id)}
            >
              {formatDate(record?.last_used, record?.time_zone_abbr)}
            </StyledDate>
          ),
          sortValue: record?.last_used
        },
        days_since_last_used: record?.days_since_last_used || record?.days_since_last_used === 0 ? record?.days_since_last_used : '-',
        app_version: record?.app_version || '-',
        os_version: record?.os_version || '-',
        restaurant: {
          display: (
            <RenderList data={record?.restaurants?.map((res) => res?.name)}/>
          ),
          sortValue: record?.restaurants?.[0]?.name
        },
      };
    });
  };

  const rows = TableRows(mobileUserActivityData?.data);

  const isCustomDateRangeValid =
    filters?.dateFilter !== "Custom Date" ||
    isValidCustomDateRange(filters?.customDateFrom, filters?.customDateTo);

  const isUpdateDisabled =
    filters?.dateFilter === "Custom Date" &&
    !isCustomDateRangeValid;

  return (
    <MobileUserActivityContainer>
      {/* Bread Crumbs */}
      <StyledBreadCrumbContainer>
        <Breadcrumbs>
          <NavigableBreadcrumbLink LinkComponent={StyledLink} label="Home" to="/admin/dashboard" />
          <StyledLink color={"#515152"}>Reports</StyledLink>
          <StyledLink color={"#515152"}>Employee Reports</StyledLink>
          <StyledLink color={"#515152"}>Mobile User Activity Report</StyledLink>
        </Breadcrumbs>
      </StyledBreadCrumbContainer>

      {/* Filter Section */}
      <FilterContainer>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", md: "center" },
            gap: { xs: 2, md: 0 }
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: { xs: 2, sm: "18px" },
              flexWrap: "wrap",
              width: { xs: "100%", md: "auto" },
              flex: 1
            }}
          >
            <Typography
              variant="body2"
              sx={{
                ...viewTypographyStyles,
                alignSelf: { xs: "flex-start", sm: "center" },
                whiteSpace: "nowrap"
              }}
            >
                View
            </Typography>

            <Tooltip title="Filters use Central Time (CDT/CST)." arrow>
              <FormControl
                size="small"
                sx={{
                  width: { xs: "100%", sm: "auto" },
                  minWidth: { xs: "100%", sm: "160px" }
                }}
              >
                <StyledInputLabel>Date Range</StyledInputLabel>
                <Select
                  value={filters?.dateFilter || ALL_VALUE}
                  renderValue={() => filters?.dateFilter}
                  label='Date Range'
                  size="small"
                  MenuProps={{
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "left",
                    },
                    transformOrigin: {
                      vertical: "top",
                      horizontal: "left",
                    },
                    PaperProps: { sx: { width: "230px" } },
                    autoFocus: false,
                  }}
                  sx={{
                    ...selectBaseStyles,
                    minWidth: { xs: "100%", sm: "160px" },
                    borderColor: "#2b4fb9",
                  }}
                  onChange={(e) => handleDateChange("dateFilter", e.target.value)}
                  endAdornment = {
                    filters?.dateFilter !== ALL_VALUE ? (
                    <InputAdornment position="end" sx={{ position: 'absolute', right: 25 }}>
                      <IconButton
                        edge='end'
                        size="small"
                        disableRipple
                        onClick={handleDateClear}
                      >
                        <ClearIcon sx={{fontSize: '20px'}}/>
                      </IconButton>
                    </InputAdornment>
                    ) : (
                      null
                    )
                  }
                >
                  {/* All option */}
                  <StyledMenuItem
                    value={ALL_VALUE}
                  >
                    {filters?.dateFilter === ALL_VALUE ? <SelectedMenuItem data="All" /> : "All"}
                  </StyledMenuItem>
                  {Days.map((value) => (
                    <StyledMenuItem
                      value={value}
                      key={value}
                    >
                      {
                        filters?.dateFilter === value ? (
                          <SelectedMenuItem data={value} />
                        ) : (
                          <>
                            {value}
                          </>
                        )
                      }
                    </StyledMenuItem>
                  ))}
                </Select>
              </FormControl>
            </Tooltip>
            {filters?.dateFilter === "Custom Date" && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  flexWrap: { xs: "wrap", sm: "nowrap" },
                  width: { xs: "100%", sm: "auto" }
                }}
              >
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <Typography variant="body2" sx={viewTypographyStyles}>
                    Days
                  </Typography>
                  <DatePicker
                    format="MM-DD-YYYY"
                    sx={{
                      ...datePickerStyles,
                      width: { xs: "100%", sm: "140px", lg: '160px' }
                    }}
                    slotProps={{ textField: { size: "small" } }}
                    value={filters.customDateFrom || null}
                    onChange={(date) =>
                      handleDateChange("customDateFrom", date)
                    }
                  />
                  <Typography variant="body2" sx={viewTypographyStyles}>
                    through
                  </Typography>
                  <DatePicker
                    sx={{
                      ...datePickerStyles,
                      width: { xs: "100%", sm: "140px", lg: '160px' }
                    }}
                    format="MM-DD-YYYY"
                    slotProps={{ textField: { size: "small" } }}
                    value={filters.customDateTo || null}
                    onChange={(date) =>
                      handleDateChange("customDateTo", date)
                    }
                  />
                </LocalizationProvider>
              </Box>
            )}

            <FormControl
              size="small"
              sx={{
                width: { xs: "100%", sm: "auto" },
                minWidth: { xs: "100%", sm: "160px" }
              }}
            >
              <StyledInputLabel>Restaurant Group</StyledInputLabel>
              <Select
                size="small"
                label="Restaurant Group"
                MenuProps={{
                  anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "left",
                  },
                  transformOrigin: {
                    vertical: "top",
                    horizontal: "left",
                  },
                  PaperProps: {
                    sx: { maxHeight: "40vh" },
                    ref: restaurantGroupMenuRef,
                  },
                  autoFocus: false,
                  MenuListProps: { sx: { pt: 0 } },
                }}
                sx={{
                  ...selectBaseStyles,
                  minWidth: { xs: "100%", sm: "160px" },
                  width: { xs: "100%", sm: "160px" },
                  borderColor: "#2b4fb9",
                }}
                onChange={handleRestaurantGroupChange}
                onClose={() => setTimeout(() => setRestaurantGroupSearch(''), 1000)}
                value={hasSingleAvailableRestaurantGroup
                  ? [getSingleItemValue(restaurantGroups)?.label]
                  : (filters?.restaurant_group_id || [ALL_VALUE])}
                multiple
                renderValue={() => {
                  if (hasSingleAvailableRestaurantGroup) {
                    return getSingleItemValue(restaurantGroups)?.value || [ALL_VALUE];
                  }
                  return getRestaurantGroupDisplayValue();
                }}
                endAdornment = {
                  !filters?.restaurant_group_id?.includes(ALL_VALUE) && !hasSingleAvailableRestaurantGroup ? (
                  <InputAdornment position="end" sx={{ position: 'absolute', right: 25 }}>
                    <IconButton
                      edge='end'
                      size="small"
                      disableRipple
                      onClick={handleRestaurantGroupClear}
                    >
                      <ClearIcon sx={{fontSize: '20px'}}/>
                    </IconButton>
                  </InputAdornment>
                  ) : (
                    null
                  )
                }
              >
                <DropdownSearchBar changeSearchText={setRestaurantGroupSearch} placeholder={'Search restaurant group'} isFetching={restaurantGroupFetching} />
                {/* All option */}
                {!hasSingleAvailableRestaurantGroup && (<StyledMenuItem
                  key={ALL_VALUE}
                  value={ALL_VALUE}
                  disabled={!filters?.restaurant_group_id?.includes(ALL_VALUE)}
                >
                  {filters?.restaurant_group_id?.includes(ALL_VALUE) ? <SelectedMenuItem data="All" /> : "All"}
                </StyledMenuItem>
                )}
                {restaurantGroups.map((res) => (
                  <StyledMenuItem
                    key={res?.label}
                    value={res?.label}
                  >
                    {
                      hasSingleAvailableRestaurantGroup || filters?.restaurant_group_id?.includes(res?.label) ? <SelectedMenuItem data={res?.value} /> : res?.value
                    }
                  </StyledMenuItem>
                ))}
                <DropdownInfiniteLoader
                  hasNextPage={hasNextRestaurantGroupsPage}
                  targetRef={restaurantGroupLoaderRef}
                />
              </Select>
            </FormControl>

            <FormControl
              size="small"
              sx={{
                width: { xs: "100%", sm: "auto" },
                minWidth: { xs: "100%", sm: "200px" }
              }}
            >
              <StyledInputLabel>Restaurant</StyledInputLabel>
              <Select
                size="small"
                label="Restaurant"
                MenuProps={{
                  anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "left",
                  },
                  transformOrigin: {
                    vertical: "top",
                    horizontal: "left",
                  },
                  PaperProps: {
                    sx: { maxHeight: "40vh" },
                    ref: restaurantMenuRef,
                  },
                  autoFocus: false,
                  MenuListProps: { sx: { pt: 0 } },
                }}
                sx={{
                  ...selectBaseStyles,
                  minWidth: { xs: "100%", sm: "200px" },
                  borderColor: "#2b4fb9",
                }}
                onChange={handleRestaurantChange}
                onClose={() => setTimeout(() => setRestaurantSearch(''), 1000)}
                value={hasSingleAvailableRestaurant
                  ? [getSingleItemValue(restaurants)?.id]
                  : (filters?.restaurant_id || [ALL_VALUE])}
                multiple
                renderValue={() => {
                  if (hasSingleAvailableRestaurant) {
                    return getSingleItemValue(restaurants)?.name || 'All';
                  }
                  return getRestaurantDisplayValue();
                }}
                endAdornment = {
                  !filters?.restaurant_id?.includes(ALL_VALUE) && !hasSingleAvailableRestaurant ? (
                  <InputAdornment position="end" sx={{ position: 'absolute', right: 25 }}>
                    <IconButton
                      edge='end'
                      size="small"
                      disableRipple
                      onClick={handleRestaurantClear}
                    >
                      <ClearIcon sx={{fontSize: '20px'}}/>
                    </IconButton>
                  </InputAdornment>
                  ) : (
                    null
                  )
                }
              >
                <DropdownSearchBar changeSearchText={setRestaurantSearch} placeholder={'Search restaurant'} isFetching={restaurantFetching} />
                {/* All option */}
                {!hasSingleAvailableRestaurant && (<StyledMenuItem
                  key={ALL_VALUE}
                  value={ALL_VALUE}
                  disabled={!filters?.restaurant_id?.includes(ALL_VALUE)}
                >
                  {filters?.restaurant_id?.includes(ALL_VALUE) ? <SelectedMenuItem data="All" /> : "All"}
                </StyledMenuItem>
                )}
                {restaurants.map((res) => (
                  <StyledMenuItem
                    key={res?.id}
                    value={res?.id}
                  >
                    {
                      hasSingleAvailableRestaurant || filters?.restaurant_id?.includes(res?.id) ? <SelectedMenuItem data={res?.name} /> : res?.name
                    }
                  </StyledMenuItem>
                ))}
                <DropdownInfiniteLoader
                  hasNextPage={hasNextRestaurantsPage}
                  targetRef={restaurantLoaderRef}
                />
              </Select>
            </FormControl>

            <FormControl
              size="small"
              sx={{
                width: { xs: "100%", sm: "auto" },
                minWidth: { xs: "100%", sm: "200px" }
              }}
            >
              <StyledInputLabel>Platform</StyledInputLabel>
              <Select
                size="small"
                label="Platform"
                MenuProps={{
                  anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "left",
                  },
                  transformOrigin: {
                    vertical: "top",
                    horizontal: "left",
                  },
                  PaperProps: { sx: { maxHeight: "40vh", width: '200px' } },
                  autoFocus: false,
                }}
                sx={{
                  ...selectBaseStyles,
                  minWidth: { xs: "100%", sm: "200px" },
                  borderColor: "#2b4fb9",
                }}
                onChange={handlePlatformChange}
                value={filters?.platform || ALL_VALUE}
                renderValue={() => getPlatformName(filters?.platform)}
                endAdornment = {
                  filters?.platform !== ALL_VALUE ? (
                  <InputAdornment position="end" sx={{ position: 'absolute', right: 25 }}>
                    <IconButton
                      edge='end'
                      size="small"
                      disableRipple
                      onClick={handlePlatformClear}
                    >
                      <ClearIcon sx={{fontSize: '20px'}}/>
                    </IconButton>
                  </InputAdornment>
                  ) : (
                    null
                  )
                }
              >
                {/* All option */}
                <StyledMenuItem
                  value={ALL_VALUE}
                >
                  {filters?.platform === ALL_VALUE ? <SelectedMenuItem data="All" /> : "All"}
                </StyledMenuItem>
                {
                  platform?.map((platformType) => (
                    <StyledMenuItem
                      key={platformType?.value}
                      value={platformType?.value}
                    >
                      {
                        filters?.platform === platformType?.value ? <SelectedMenuItem data={platformType?.label} />  : platformType?.label
                      }
                    </StyledMenuItem>
                  ))
                }
              </Select>
            </FormControl>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              alignSelf: { xs: "flex-start", md: "center" },
              mt: { xs: 1, md: 0 },
              width: { xs: "100%", md: "auto" },
              justifyContent: { xs: "flex-start", md: "flex-end" }
            }}
          >
            <ResetButton
              onClick={handleReset}
              filtersChanged={filtersChanged}
              status={isFetching}
            />
            <UpdateButton
              onClick={handleChange}
              filtersChanged={filtersChanged}
              status={isFetching}
              disabled={isUpdateDisabled}
            />
          </Box>
        </Box>
      </FilterContainer>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {/* Page Title */}
        <StyledTitle>Mobile User Activity Report</StyledTitle>

        <StyledSearchBar
          data-testid="mobile-search-bar"
          value={search}
          placeholder="Search mobile users"
          InputProps={{
            startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>),
          }}
          onChange={(e) => handleSearch(e.target.value)}
          autoComplete="off"
        />
      </div>
      <Divider style={{ borderStyle: "solid", borderWidth: "1px", marginBottom: '16px' }} />

      {/* Mobile User Activity Table */}
      {
        isFetching && !search? (
          <Loader />
        ) : (
          <IndexTable
            columns={columns}
            data={rows}
            tableId="mobile-user-activity-entries"
            type="entries"
            itemsPerPage={itemsPerPage}
            setItemsPerPage={setItemsPerPage}
            page={page}
            setPage={setPage}
            pageCount={mobileUserActivityData?.pageCount}
            report={false}
            searchLoading={isFetching && !!debouncedSearchTerm}
            hasSearchTerm={!!debouncedSearchTerm}
            showDownloadButton={true}
            disableDownloadButton={mobileUserActivityData?.data?.length === 0 || isFetching || csvLoading}
            handleExportCsv={handleExportCsv}
            csvLoading={csvLoading}
            serverSort={true}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSortChange={handleSortChange}
          />
        )
      }

      { /* User Activity Log */ }
      <UserActivityLogModal
        open={openActivityLog}
        setOpen={setOpenActivityLog}
        data={userActivityLog?.data}
        isLoading={activityLogLoading}
        modalPage={modalPage}
        setModalPage={setModalPage}
        modalPerPage={modalPerPage}
        setModalPerPage={setModalPerPage}
        modalPageCount={userActivityLog?.pageCount}
      />
    </MobileUserActivityContainer>
  )
}
